import React from "react";
import filter from "ramda/src/filter";
import map from "ramda/src/map";
import compose from "ramda/src/compose";
import { Grid } from "../../UI/Grid";
import { Select } from "../../UI/Select";
import { Button } from "../../UI/Button";
import { PlacementWorksheetBottomPopup } from "../PlacementWorksheetBottomPopup";
import useAuth from "../../routes/login/AuthContext";
import { useSelectedNetworkContext } from "../../contexts/NetworkContext/context";
import { usePlacementWorksheetContext } from "../../contexts/PlacementWorksheetContext";
import { useSnackbar } from "../../hooks/useSnackbar";
import { OptionType } from "@atlaskit/select";
import {
  IPlacementWorksheetUnit,
  putPlacementWorksheetOccupantsClear,
  putPlacementWorksheetSettingsUnitNumberClear,
} from "../../services/PlacementWorksheetApi";
import { normalize } from "../../helpers/normalize";
import { UserRoommateLockModal } from "../UserRoommateLockModal";
import { useModalContext } from "../../contexts/ModalContext/context";
// import { ModalProvider } from "../../contexts/ModalContext";
// import { ClearAllDataPopupButton } from "./ClearAllDataPopupButton";
import { pathOrArray } from "../../helpers/pathOrArray";
import { propId } from "../../helpers/propId";

const UNIT_CONTROL_OPTIONS = [{ value: "clear", label: "Clear unit number" }];

const RESIDENT_CONTROL_OPTIONS = [
  { value: "clear", label: "Return all to Unplaced" },
  // { value: "remove", label: "Remove residents" },
  { value: "lock", label: "Lock in matches" },
  { value: "unlock", label: "Unlock matches" },
  // { value: "combine", label: "Combine" },
];

interface IProps {
  isOpen: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
  clearSelection: VoidFunction;
  selectedRows: number[];
}

export const PSTableRowSelectBottomPopup: React.FC<IProps> = (props) => {
  const { getAuthToken, isSuperAdminRead } = useAuth();
  const { setIsOpen } = useModalContext();
  const [adminLock, setAdminLock] = React.useState(false);
  const { selectedNetworkId } = useSelectedNetworkContext();
  const [unitAction, setUnitAction] = React.useState<OptionType | null>();
  const [residentsAction, setResidentsAction] = React.useState<OptionType | null>();
  const { l2Id, l3Id, occupants } = usePlacementWorksheetContext();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();

  React.useEffect(() => {
    if (props.isOpen) return;
    setResidentsAction(null);
    setUnitAction(null);
  }, [props.isOpen]);

  const getOccupantsIds = compose(map(map(propId)), map(pathOrArray(["occupants"])));
  const filterUnits = filter<IPlacementWorksheetUnit>(
    (occupant) => !!occupant?.unitId && props.selectedRows.includes(occupant.unitId)
  );

  const getMembershipsIds = compose(
    getOccupantsIds,
    // @ts-ignore
    filterUnits
  );

  const handleClearSeletectedUnitOccupants = React.useCallback(async () => {
    if (!l2Id || !l3Id) {
      return;
    }

    try {
      const token = await getAuthToken();
      const res = await putPlacementWorksheetOccupantsClear(selectedNetworkId, l2Id, l3Id, props.selectedRows, token);
      const { error } = res;
      if (error) {
        throw error;
      }
      showSuccessSnackbar("Successfully updated");
      props.onSuccess();
    } catch (err) {
      showErrorSnackbar();
    }
  }, [selectedNetworkId, l2Id, l3Id, normalize(props.selectedRows)]);

  const handleClearSeletectedUnitNumbers = React.useCallback(async () => {
    if (!l2Id || !l3Id) {
      return;
    }
    try {
      const token = await getAuthToken();
      const res = await putPlacementWorksheetSettingsUnitNumberClear(
        selectedNetworkId,
        l2Id,
        l3Id,
        props.selectedRows,
        token
      );
      const { error } = res;
      if (error) {
        throw error;
      }
      showSuccessSnackbar("Successfully updated");
      props.onSuccess();
    } catch (err) {
      showErrorSnackbar();
    }
  }, [selectedNetworkId, l2Id, l3Id, normalize(props.selectedRows)]);

  const handleResidentsSubmit = async () => {
    if (residentsAction?.value === "clear") {
      return handleClearSeletectedUnitOccupants();
    }

    if (residentsAction?.value === "lock") {
      setAdminLock(false);
      setIsOpen(true);
      return;
    }

    if (residentsAction?.value === "unlock") {
      setAdminLock(true);
      setIsOpen(true);
      return;
    }
  };

  const handleUnitSubmit = async () => {
    if (unitAction?.value === "clear") {
      return handleClearSeletectedUnitNumbers();
    }
  };

  return (
    <PlacementWorksheetBottomPopup open={props.isOpen} onClose={props.onClose}>
      <Grid container justifyContent="space-between" alignItems="center" px={5} minHeight="15vh" spacing={2}>
        <Grid item>
          <Select
            value={unitAction}
            placeholder="Unit #"
            menuPosition="fixed"
            onChange={setUnitAction}
            options={UNIT_CONTROL_OPTIONS}
          />
        </Grid>
        <Grid item>
          <Button
            appearance="primary"
            onClick={handleUnitSubmit}
            isDisabled={!unitAction?.value || unitAction?.value === "default" || isSuperAdminRead()}
          >
            Go
          </Button>
        </Grid>
        <Grid item>
          <Select
            value={residentsAction}
            placeholder="Residents"
            menuPosition="fixed"
            options={RESIDENT_CONTROL_OPTIONS}
            onChange={setResidentsAction}
          />
        </Grid>
        <Grid item>
          <Button
            appearance="primary"
            onClick={handleResidentsSubmit}
            isDisabled={!residentsAction?.value || residentsAction?.value === "default" || isSuperAdminRead()}
          >
            Go
          </Button>
          <UserRoommateLockModal
            l1Id={selectedNetworkId}
            l2Id={l2Id}
            l3Id={l3Id}
            adminLock={adminLock}
            onSuccess={props.onSuccess}
            // @ts-ignore
            membershipIdsGroups={getMembershipsIds(occupants?.assignedUnits || []) as any}
          />
        </Grid>
        <Grid item xs />
        {/* <Grid item>
          <ModalProvider>
            <ClearAllDataPopupButton onSuccess={props.onSuccess} />
          </ModalProvider>
        </Grid> */}
        <Grid item>
          <Button onClick={props.clearSelection}>Unselect</Button>
        </Grid>
      </Grid>
    </PlacementWorksheetBottomPopup>
  );
};
